import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { canAccessCorporatePurchasing } from "../../corporate-purchasing/corporatePurchasingAccess";
import { apiError } from "../../purchasing/utils/tenderManagement";
import useManagementDialog from "../../purchasing/hooks/useManagementDialog";
import CatalogRecordActions from "./CatalogRecordActions";
import UnitVisibilityCheckbox, { saveUnitVisibility } from "../../purchasing/components/UnitVisibilityCheckbox";
import UnitAliasDialog from "../../purchasing/components/popups/UnitAliasDialog";
import CorporatePagination from "./CorporatePagination";
import CorporatePicker from "./CorporatePicker";
import { getOfficialUnitName, getUnitTranslations, unitSearchText, UNIT_LANGUAGES, UNIT_LANGUAGE_LABELS } from "../../purchasing/utils/unitNames";
import { getUnitMessages } from "../../purchasing/components/unitMessages";
import "../../purchasing/pages/Tenders.css";
import "./CorporatePurchasing.css";

const BASE = "/api/admin/purchasing";
const accessKey = (user) => JSON.stringify([user?.id, user?.business_unit_ids, user?.has_access_to_all_business_units, user?.corporate_modules]);

function UnitEditor({ record, onClose, onSaved }) {
  const { languageId } = usePmsTheme();
  const words = getUnitMessages(languageId);
  const [form, setForm] = useState(() => ({ code: "", type: "count", ...record, name_translations: getUnitTranslations(record) }));
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const ref = useManagementDialog(onClose, busy);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    const name_translations = Object.fromEntries(UNIT_LANGUAGES.map((language) => [language, form.name_translations[language].trim()]));
    if (!form.code.trim() || !Object.values(name_translations).some(Boolean)) { setError(words.required); return; }
    setBusy(true); setError("");
    try {
      const body = record.is_predefined ? { name_translations } : { code: form.code, type: form.type, name_translations };
      if (record.id) await axios.put(`${BASE}/units/${record.id}`, body);
      else await axios.post(`${BASE}/units`, body);
      await onSaved(); onClose();
    } catch (failure) { setError(apiError(failure, languageId)); } finally { setBusy(false); }
  };
  return <div className="pt-modal" ref={ref} role="dialog" aria-modal="true" aria-label={record?.id ? "Edit unit of measurement" : "New unit of measurement"} tabIndex={-1}>
    <form className="pt-card" onSubmit={save}><h2>{record?.id ? "Edit" : "New"} unit of measurement</h2>
      <p className="pt-hint">Define a measure or purchase format, such as kilogram, litre, bottle or box. This definition is shared across hotels.</p>
      {error && <div role="alert" className="pt-notice pt-notice--error">{error}</div>}
      <div className="pt-grid"><label>{words.code}<input required maxLength={20} value={form.code} onChange={(e) => set("code", e.target.value)} disabled={busy || Boolean(record?.is_predefined)} /></label>
        <CorporatePicker label={words.type} value={form.type} disabled={busy || Boolean(record?.is_predefined)}
          options={Object.entries(words.types).map(([value, label]) => ({ value, label }))} onChange={value => set("type", value || "count")} />
      </div>
      <p className="pt-hint" id="unit-names-hint">{words.namesHint}</p>
      <div className="pt-grid">{UNIT_LANGUAGES.map((language) => <label key={language}>{UNIT_LANGUAGE_LABELS[language]}<input lang={language} maxLength={100} value={form.name_translations[language]} aria-describedby="unit-names-hint" disabled={busy} onChange={(event) => set("name_translations", { ...form.name_translations, [language]: event.target.value })} /></label>)}</div>
      {record?.is_predefined && <p className="pt-hint">{words.protectedHint}</p>}
      <div className="pt-actions"><button type="button" disabled={busy} onClick={onClose}>{words.cancel}</button><button className="pt-primary" disabled={busy}>{busy ? words.saving : words.save}</button></div>
    </form>
  </div>;
}
export default function PurchasingMeasurementUnits({ masterCatalog = false }) {
  const { themeId } = usePmsTheme();
  const user = readStoredUser();
  const authorized = masterCatalog ? Number(user?.id) === 1 : canAccessCorporatePurchasing(user);
  if (!authorized) return <div className="cp-workspace" data-theme={themeId}><div className="cp-empty">{masterCatalog
    ? "Administrator access is required." : "Corporate purchasing access is required."}</div></div>;
  return <Measurements key={JSON.stringify([masterCatalog, accessKey(user)])} masterCatalog={masterCatalog} user={user} />;
}

function Measurements({ masterCatalog, user }) {
  const { themeId, languageId } = usePmsTheme();
  const words = getUnitMessages(languageId);
  const [units, setUnits] = useState([]), [businessUnits, setBusinessUnits] = useState([]);
  const [hotelId, setHotelId] = useState(null);
  const [loading, setLoading] = useState(true), [error, setError] = useState("");
  const [search, setSearch] = useState(""), [status, setStatus] = useState("all"), [editor, setEditor] = useState(null);
  const [requestedPage, setPage] = useState(1), [pageSize, setPageSize] = useState(25);
  const loadEpoch = useRef(0);
  const load = useCallback(async () => {
    const epoch = ++loadEpoch.current;
    setLoading(true); setError("");
    try {
      const [measurements, businesses] = await Promise.all([
        axios.get(`${BASE}/units`, { params: { settings: true, ...(hotelId ? { business_unit_id: hotelId } : {}) } }),
        masterCatalog ? null : axios.get(`${BASE}/business-units`),
      ]);
      if (epoch !== loadEpoch.current) return;
      setUnits((measurements.data || []).filter((unit) => unit.source === "purchasing" && unit.business_unit_id == null));
      setBusinessUnits(businesses?.data || []);
    } catch (failure) { if (epoch === loadEpoch.current) setError(apiError(failure, languageId)); }
    finally { if (epoch === loadEpoch.current) setLoading(false); }
  }, [hotelId, masterCatalog, languageId]);
  useEffect(() => { load(); return () => { loadEpoch.current += 1; }; }, [load]);
  const visible = useMemo(() => units.filter((unit) => unitSearchText(unit).toLowerCase().includes(search.trim().toLowerCase()) &&
    (status === "all" || (status === "active" ? unit.is_active !== false : unit.is_active === false))), [units, search, status]);
  const page = Math.min(requestedPage, Math.max(1, Math.ceil(visible.length / pageSize)));
  const selectedHotel = businessUnits.find((bu) => bu.id === hotelId);
  const changeHotel = (id) => {
    if ((id || null) === hotelId) return;
    loadEpoch.current += 1;
    setEditor(null); setUnits([]); setPage(1); setHotelId(id || null);
  };
  const saveVisibility = async (unit, hidden) => {
    if (unit.can_change_visibility === false || accessKey(readStoredUser()) !== accessKey(user)) return;
    const epoch = loadEpoch.current;
    const data = await saveUnitVisibility(`${BASE}/units/${unit.id}/visibility`,
      { is_hidden: hidden, ...(hotelId ? { business_unit_id: hotelId } : {}) }, { pilcoreExpectedUserId: user.id });
    if (epoch === loadEpoch.current && accessKey(readStoredUser()) === accessKey(user)) {
      setUnits(rows => rows.map(row => row.id === unit.id ? { ...row, ...data } : row));
    }
  };
  const saveAlias = async (displayName) => {
    if (!selectedHotel || accessKey(readStoredUser()) !== accessKey(user)) return;
    await axios.put(`${BASE}/units/${editor.id}/alias`, { business_unit_id: hotelId, display_name: displayName },
      { pilcoreExpectedUserId: user.id });
    await load();
  };
  return <main className="cp-workspace cp-workspace--units pt-shell" data-theme={themeId}><header className="cp-page-head"><div>
    <div className="cp-breadcrumb">{masterCatalog ? "ADMINISTRATION" : "CORPORATE PURCHASING"}</div><h1>{words.title}</h1>
    <p>{masterCatalog ? words.catalogue : words.chooseHotel}</p></div>
    {masterCatalog && <button className="cp-button cp-button--primary" disabled={loading} onClick={() => setEditor({})}>New unit</button>}</header>
    {error && <div role="alert" className="pt-notice pt-notice--error">{error}<button onClick={load}>Retry</button></div>}
    <div className="pt-grid">{!masterCatalog && <CorporatePicker label={words.hotel} value={hotelId || 0} onChange={changeHotel} disabled={loading}
      options={[{ value: 0, label: words.corporateCatalogue }, ...businessUnits.map((bu) => ({ value: bu.id, label: bu.name }))]} />}
      <label>{words.search}<input type="search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} /></label>
      <label>Status<select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label></div>
    {!masterCatalog && !hotelId && <p className="pt-hint">{words.sharedVisibility}</p>}
    <div className="pt-scroll"><table className="pt-table"><thead><tr><th>{words.code}</th>{masterCatalog ? UNIT_LANGUAGES.map((language) => <th key={language}>{UNIT_LANGUAGE_LABELS[language]}</th>) : <th>{words.officialName}</th>}<th>{words.type}</th><th>Status</th>
      {!masterCatalog && <th>{words.hide}</th>}{selectedHotel && <th>{words.hotelName}</th>}{(masterCatalog || selectedHotel) && <th>Actions</th>}</tr></thead><tbody>
      {visible.slice((page - 1) * pageSize, page * pageSize).map((unit) => {
        const names = getUnitTranslations(unit);
        const displayName = getOfficialUnitName(unit, languageId);
        return <tr key={unit.id}><td>{unit.code}</td>{masterCatalog ? UNIT_LANGUAGES.map((language) => <td key={language} lang={language}>{names[language] || "—"}</td>) : <td>{displayName}</td>}<td>{words.types[unit.type] || unit.type}</td>
          <td>{unit.is_active === false ? "Inactive" : "Active"}</td>
          {!masterCatalog && <td><UnitVisibilityCheckbox unit={unit} name={displayName} words={words} disabled={loading}
            onSave={hidden => saveVisibility(unit, hidden)} /></td>}
          {selectedHotel && <><td>{unit.display_name || "—"}</td><td><button disabled={loading} aria-label={`${words.editAlias}: ${displayName}`}
            onClick={() => setEditor(unit)}>{words.editAlias}</button></td></>}
          {masterCatalog && <td><div className="pt-actions"><button disabled={loading} aria-label={`${words.edit}: ${displayName}`} onClick={() => setEditor(unit)}>{words.edit}</button>
            {!unit.is_predefined && <CatalogRecordActions record={unit} unit onChanged={load} disabled={loading} />}</div></td>}</tr>;
      })}
    </tbody></table></div>
    {loading ? <p role="status">{words.loading}</p> : !visible.length && <p>{words.empty}</p>}
    <CorporatePagination total={visible.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(size) => { setPageSize(size); setPage(1); }} label="units" disabled={loading} />
    {masterCatalog && editor && <UnitEditor record={editor} onClose={() => setEditor(null)} onSaved={load} />}
    {!masterCatalog && selectedHotel && editor && <UnitAliasDialog unit={editor} hotelName={selectedHotel.name} onClose={() => setEditor(null)} onSave={saveAlias} />}
  </main>;
}
